import { FocusSelectionParams } from '../../coaching-base/config';
import {
    CoachableMetric,
    CommitmentFocusSummarySetDto,
    CommitmentKpi,
} from '../../coaching-base/general';
import { GraphType } from '../../coaching-insight/oa/models/oa.model';
import { SortDirection } from '../../coaching-insight/oa/models/sorting.model';
import { FocusShallow } from '../../coaching-tags/tags/tags.model';
import {
    MetricData,
    MetricDataItem,
} from '../../performance-data/metric.pd.model';
import { createAction } from '../../store.model';
import { ObservationFormValue } from '../commitment-observations/observations.commitment.form';
import { GoalsCollection } from '../goals-collection.model';
import { CommitmentGoalHistory } from '../models/commitment-goal-history.model';
import { FocusKPI } from '../models/commitment.model';
import { GoalDataPoint } from '../models/goal-data-point.model';
import { GoalEventDataPoint } from '../models/goal-event-data-point.model';

export enum ActionType {
    COACHING_COMMITMENT__GOAL_COMMITMENT__INIT = 'COACHING_COMMITMENT__GOAL_COMMITMENT__INIT',
    COACHING_COMMITMENT__GOAL_COMMITMENT__INIT_RESULT = 'COACHING_COMMITMENT__GOAL_COMMITMENT__INIT_RESULT',
    COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA = 'COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA',
    COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA_RESULT = 'COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA_RESULT',
    COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA_CANCEL = 'COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA_CANCEL',
    COACHING_COMMITMENT__GOAL_COMMITMENT__LOAD_GOALS = 'COACHING_COMMITMENT__GOAL_COMMITMENT__LOAD_GOALS',
    COACHING_COMMITMENT__GOAL_COMMITMENT__LOAD_GOALS_RESULT = 'COACHING_COMMITMENT__GOAL_COMMITMENT__LOAD_GOALS_RESULT',
    COACHING_COMMITMENT__GOAL_COMMITMENT__SORT_GOALS = 'COACHING_COMMITMENT__GOAL_COMMITMENT__SORT_GOALS',
    COACHING_COMMITMENT__GOAL_COMMITMENT__ADD_STATE_GOAL = 'COACHING_COMMITMENT__GOAL_COMMITMENT__ADD_STATE_GOAL',
    COACHING_COMMITMENT__GOAL_COMMITMENT__UPDATE_STATE_GOAL = 'COACHING_COMMITMENT__GOAL_COMMITMENT__UPDATE_STATE_GOAL',
    COACHING_COMMITMENT__GOAL_COMMITMENT__MERGE_CHANGED_FOCUS_KPIS = 'COACHING_COMMITMENT__GOAL_COMMITMENT__MERGE_CHANGED_FOCUS_KPIS',
    COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_HISTORY = 'COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_HISTORY',
    COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_HISTORY_RESULT = 'COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_HISTORY_RESULT',
    COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_EVENT_HISTORY = 'COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_EVENT_HISTORY',
    COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_EVENT_HISTORY_RESULT = 'COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_EVENT_HISTORY_RESULT',
    COACHING_COMMITMENT__GOAL_COMMITMENT__FILL_FOCUS_KPI_PERFORMANCE = 'COACHING_COMMITMENT__GOAL_COMMITMENT__FILL_FOCUS_KPI_PERFORMANCE',
    COACHING_COMMITMENT__GOAL_COMMITMENT_ROOT__CLEAR_STATE = 'COACHING_COMMITMENT__GOAL_COMMITMENT_ROOT__CLEAR_STATE',
}

export interface GoalCommitmentInitAction {
    performerId: number;
    focusSelectionParams?: FocusSelectionParams;
}

export const goalCommitmentInit = createAction<
    ActionType,
    GoalCommitmentInitAction
>(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__INIT);

export interface GoalCommitmentInitResultAction
    extends GoalCommitmentInitAction {
    metrics?: CoachableMetric[];
    focusSummarySet?: CommitmentFocusSummarySetDto;
}

export const goalCommitmentInitResult = createAction<
    ActionType,
    GoalCommitmentInitResultAction
>(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__INIT_RESULT);

/// /////////////////

export interface GoalCommitmentRetrieveDataAction {
    graphType: GraphType;
    opts: any;
}

export const goalCommitmentRetrieveData = createAction<
    ActionType,
    GoalCommitmentRetrieveDataAction
>(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA);

export interface GoalCommitmentRetrieveDataResultAction
    extends GoalCommitmentRetrieveDataAction {
    metricData?: MetricData;
}

export const goalCommitmentRetrieveDataResult = createAction<
    ActionType,
    GoalCommitmentRetrieveDataResultAction
>(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA_RESULT);

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface GoalCommitmentRetrieveDataCancelAction {}

export const goalCommitmentRetrieveDataCancel = createAction<
    ActionType,
    GoalCommitmentRetrieveDataCancelAction
>(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA_CANCEL);

/// /////////////////

export interface LoadGoals {
    goalsCollection?: GoalsCollection;
    focus?: FocusShallow;
    performerId?: number;
    commitmentId?: number;
    linkedObservations?: ObservationFormValue[];
}

export const loadGoals = createAction<ActionType, LoadGoals>(
    ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__LOAD_GOALS,
);

/// /////////////////

export interface LoadGoalsResult extends LoadGoals {
    focusKpis?: FocusKPI[];
    otherCommitmentKpis?: CommitmentKpi[];
}

export const loadGoalsResult = createAction<ActionType, LoadGoalsResult>(
    ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__LOAD_GOALS_RESULT,
);

/// /////////////////

export interface GoalCommitmentSortGoalsAction {
    direction: SortDirection;
}

export const goalCommitmentSortGoals = createAction<
    ActionType,
    GoalCommitmentSortGoalsAction
>(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__SORT_GOALS);

/// /////////////////

export interface GoalCommitmentAddStateGoalAction {
    goal: CommitmentKpi;
}

export const goalCommitmentAddStateGoal = createAction<
    ActionType,
    GoalCommitmentAddStateGoalAction
>(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__ADD_STATE_GOAL);

/// /////////////////

export interface GoalCommitmentUpdateStateGoalAction {
    index: number;
    goal: CommitmentKpi;
    goalStatusId?: number;
    isPlaceholderMetric?: boolean;
}

export const goalCommitmentUpdateStateGoal = createAction<
    ActionType,
    GoalCommitmentUpdateStateGoalAction
>(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__UPDATE_STATE_GOAL);

/// /////////////////

export interface GoalCommitmentMergeChangedFocusKPIsAction {
    linkedObservations?: ObservationFormValue[];
    lastLinkedObservation?: ObservationFormValue;
    placeholderMetrics?: number[];
}

export const goalCommitmentMergeChangedFocusKPIs = createAction<
    ActionType,
    GoalCommitmentMergeChangedFocusKPIsAction
>(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__MERGE_CHANGED_FOCUS_KPIS);

/// /////////////////

export interface GoalCommitmentRetrieveGoalHistoryAction {
    commitmentId?: number;
    performerId?: number;
    goalId?: number;
}

export const goalCommitmentRetrieveGoalHistory = createAction<
    ActionType,
    GoalCommitmentRetrieveGoalHistoryAction
>(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_HISTORY);

export interface GoalCommitmentRetrieveGoalHistoryResultAction
    extends GoalCommitmentRetrieveGoalHistoryAction {
    goalHistory?: CommitmentGoalHistory[];
    goalDataPoints?: GoalDataPoint[];
}

export const goalCommitmentRetrieveGoalHistoryResult = createAction<
    ActionType,
    GoalCommitmentRetrieveGoalHistoryResultAction
>(
    ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_HISTORY_RESULT,
);

/// ////////////////

export interface GoalCommitmentRetrieveGoalEventHistoryAction {
    commitmentId?: number;
    performerId?: number;
    goalId?: number;
    linkedObservations?: ObservationFormValue[];
}

export const goalCommitmentRetrieveGoalEventHistory = createAction<
    ActionType,
    GoalCommitmentRetrieveGoalEventHistoryAction
>(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_EVENT_HISTORY);

export interface GoalCommitmentRetrieveGoalEventHistoryResultAction
    extends GoalCommitmentRetrieveGoalEventHistoryAction {
    goalEventDataPoints?: GoalEventDataPoint[];
}

export const goalCommitmentRetrieveGoalEventHistoryResult = createAction<
    ActionType,
    GoalCommitmentRetrieveGoalEventHistoryResultAction
>(
    ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_EVENT_HISTORY_RESULT,
);

/// /////////////////

export interface GoalCommitmentFillFocusKPIPerformanceAction {
    metrics?: MetricDataItem[];
}

export const fillFocusKPIPerformance = createAction<
    ActionType,
    GoalCommitmentFillFocusKPIPerformanceAction
>(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__FILL_FOCUS_KPI_PERFORMANCE);

/// /////////////////

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface RootClearStateAction {}

export const clearState = createAction<ActionType, RootClearStateAction>(
    ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT_ROOT__CLEAR_STATE,
);
